/* HEMKIND flexible packs. Shopify remains the authority for prices and discounts. */
(() => {
  'use strict';
  if (customElements.get('hemkind-packs')) return;

  class HemkindPacks extends HTMLElement {
    static aggregate(variants, copy = () => '') {
      const items = new Map();
      for (const variant of variants) {
        if (!variant || !variant.available) throw new Error(copy('choose_available'));
        const id = String(variant.id);
        const existing = items.get(id);
        items.set(id, { id: Number(id), quantity: (existing?.quantity || 0) + 1 });
      }
      return [...items.values()];
    }

    static rateFor(count, rates, verified) {
      if (!verified || count < 2) return 0;
      const values = [2, 3, 4].map(key => Number(rates[key]));
      if (!Number.isFinite(count) || values.some(value => !Number.isFinite(value) || value < 0 || value >= 100)) return 0;
      // Minimum-quantity rules: the best eligible percentage applies. A zero
      // tier does not disable other configured tiers, even when an option is hidden.
      return Math.max(0, ...values.slice(0, Math.min(Math.floor(count), 4) - 1));
    }

    static quote(variants, rate) {
      const original = variants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
      const total = variants.reduce((sum, variant) => sum + Math.round(Number(variant?.price || 0) * (100 - rate) / 100), 0);
      return { original, total, saving: original - total };
    }

    static cartQuantity(cart, id) {
      return (cart?.items || []).filter(item => String(item.variant_id || item.id) === String(id)).reduce((sum, item) => sum + Number(item.quantity), 0);
    }

    static assertQuantities(items, variants, cart, copy = () => '') {
      for (const item of items) {
        const variant = variants.find(value => String(value.id) === String(item.id));
        const existing = HemkindPacks.cartQuantity(cart, item.id);
        const quantity = item.quantity + existing;
        const maximum = variant.max == null ? Infinity : Number(variant.max);
        const stock = variant.stock == null ? Infinity : Number(variant.stock);
        if (quantity > Math.min(maximum, stock)) throw new Error(copy('insufficient_stock', { COLOR: variant.color, SIZE: variant.size }));
        if (quantity < Number(variant.min || 1) || quantity % Number(variant.step || 1) !== 0) throw new Error(copy('quantity_unavailable', { COLOR: variant.color, SIZE: variant.size }));
      }
    }

    copy(key, values = {}) {
      let message = this.data?.labels?.[key] || '';
      for (const [token, value] of Object.entries(values)) message = message.replaceAll('__' + token + '__', String(value));
      return message;
    }

    connectedCallback() {
      if (!this.lifecycleAbort) {
        this.lifecycleAbort = new AbortController();
        const signal = this.lifecycleAbort.signal;
        document.addEventListener('DOMContentLoaded', () => this.startInitialization(), { signal });
        document.addEventListener('shopify:section:load', event => {
          if (event.target === this || event.target?.contains?.(this)) this.startInitialization();
        }, { signal });
      }
      this.startInitialization();
    }

    startInitialization() {
      this.stopInitialization();
      if (this.abort || !this.isConnected) return;
      const run = this.initializationRun;
      // The editor can connect this element before its JSON, controls or native
      // buy form exist. Observe assembly, with a bounded six-second fallback.
      let remaining = 24;
      const attempt = () => {
        if (run !== this.initializationRun) return;
        if (!this.isConnected) { this.stopInitialization(); return; }
        if (this.init()) { this.stopInitialization(); return; }
        if (--remaining <= 0) {
          this.stopInitialization();
          this.editorStatus(this.initError || 'The pack selector could not initialize. Refresh this product preview.');
          return;
        }
        this.retryTimer = setTimeout(attempt, 250);
      };
      this.initObserver = new MutationObserver(() => {
        if (run !== this.initializationRun) return;
        if (this.isConnected && this.init()) this.stopInitialization();
      });
      this.initObserver.observe(this.closest('[id^="MainProduct-"]') || this, { childList: true, subtree: true });
      queueMicrotask(attempt);
    }

    stopInitialization() {
      this.initializationRun = (this.initializationRun || 0) + 1;
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.initObserver?.disconnect();
      this.initObserver = null;
    }

    editorStatus(message) {
      // This separate status node is emitted by Liquid only in design mode.
      const status = this.dataset.editorStatusId && document.getElementById(this.dataset.editorStatusId);
      if (status) { status.textContent = message; status.hidden = !message; }
    }

    initializationFailure(message) {
      this.initError = message;
      return false;
    }

    init() {
      if (this.abort) return true;
      const source = this.querySelector('[data-pack-data]');
      this.root = this.closest('[id^="MainProduct-"]');
      if (!source || !this.root) return this.initializationFailure('Waiting for the product section and pack data. Refresh this product preview if it does not finish loading.');
      try { this.data = JSON.parse(source.textContent); } catch (_) { return this.initializationFailure('The pack data could not be read. Refresh this product preview.'); }
      if (!Array.isArray(this.data?.variants) || !this.data.variants.length || this.data.variants.some(variant => !variant)) return this.initializationFailure('This product has no supported color and size variants for the pack selector. Its normal purchase controls remain available.');
      const includedProducts = new Set(this.data.variants.map(variant => String(variant.productId)));
      if (!Array.isArray(this.data.productIds) || !this.data.productIds.length || this.data.productIds.some(id => !includedProducts.has(String(id))) || !includedProducts.has(String(this.data.productId))) return this.initializationFailure('The selected pack family is incomplete or does not include this product. Use the current product or select only colors of this exact model.');
      if (this.data.productIds.some(id => this.data.variants.filter(variant => String(variant.productId) === String(id)).length !== this.data.variantCounts?.[String(id)])) return this.initializationFailure('Not all variants in this pack family could be loaded. Its normal purchase controls remain available.');
      if (!this.data.rates || typeof this.data.rates !== 'object') return this.initializationFailure('The pack settings could not be read. Refresh this product preview.');
      this.allowedQuantities = this.data.allowedQuantities ?? [1, 2, 3, 4];
      if (!Array.isArray(this.allowedQuantities) || this.allowedQuantities.length < 2
        || this.allowedQuantities[0] !== 1 || new Set(this.allowedQuantities).size !== this.allowedQuantities.length
        || this.allowedQuantities.some(quantity => !Number.isInteger(quantity) || quantity < 1 || quantity > 4)) {
        return this.initializationFailure('No pack sizes are available for this product. Its normal purchase controls remain available.');
      }
      if (this.root.querySelector('hemkind-packs[data-pack-ready]')) return this.initializationFailure('Another pack selector is already active for this product. Keep one pack block per product section.');
      this.mainForm = [...this.root.querySelectorAll('form')].find(form => form.id === `product-form-${this.dataset.sectionId}`)
        || this.root.querySelector('.button-trigger__sticky form[data-type="add-to-cart-form"]');
      if (!this.mainForm || !this.mainForm.querySelector('[name="id"]') || this.mainForm.closest('sticky-add-cart')) return this.initializationFailure('The native product purchase form is not available. Keep the Buy buttons block enabled. Grouped or external products use their normal purchase flow.');
      this.builder = this.querySelector('.hk-packs__builder');
      this.slots = this.querySelector('[data-pack-slots]');
      this.addButton = this.querySelector('[data-pack-add]');
      this.sharedSize = this.querySelector('[data-pack-shared-size]');
      this.errorNode = this.querySelector('[data-pack-error]');
      this.cartLink = this.querySelector('[data-pack-cart]');
      if (![this.builder, this.slots, this.addButton, this.sharedSize, this.errorNode, this.cartLink,
        this.querySelector('[data-pack-original]'), this.querySelector('[data-pack-total]'), this.querySelector('[data-pack-saving]')].every(Boolean)
        || this.querySelectorAll('.hk-packs__quantities input').length !== this.allowedQuantities.length
        || [...this.querySelectorAll('.hk-packs__quantities input')].some(input => !this.allowedQuantities.includes(Number(input.value)))) return this.initializationFailure('Waiting for the pack controls. Refresh this product preview if they do not finish loading.');
      // Claim readiness only after every prerequisite exists, so an early
      // connection never makes a partially assembled element permanently inert.
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.count = 1;
      this.selections = [];
      this.busy = false;
      this.uncertain = false;
      this.invalid = false;
      this.sharedSize.replaceChildren();
      this.slots.replaceChildren();
      this.cartLink.hidden = true;
      this.setError('');
      this.querySelectorAll('.hk-packs__quantities input').forEach(input => { input.checked = input.value === '1'; });
      this.nativeAreas = [...this.root.querySelectorAll('.product-detail__buy-buttons')];
      this.colors = [];
      const seen = new Set();
      for (const variant of this.data.variants) {
        const key = `${variant.productId}:${variant.color}`;
        if (!seen.has(key)) {
          seen.add(key);
          this.colors.push({ key, productId: variant.productId, color: variant.color });
        }
      }
      const sizes = [...new Set(this.data.variants.map(variant => variant.size))];
      for (const size of sizes) this.sharedSize.add(new Option(size, size));
      this.sharedSize.value = this.currentVariant().size;
      this.querySelectorAll('[data-tier-label]').forEach(label => {
        const rate = HemkindPacks.rateFor(Number(label.dataset.tierLabel), this.data.rates, this.discountDisplayEnabled());
        label.textContent = rate ? this.copy('save_percent', { RATE: rate }) : this.copy('regular_price');
      });
      this.addEventListener('change', event => this.onChange(event), { signal });
      this.addButton.addEventListener('click', () => this.addSet(), { signal });
      this.createStickyButton(signal);
      this.applyMode();
      this.updateButtons();
      this.root.addEventListener('submit', event => {
        if (this.count < 2 || !this.isPurchaseForm(event.target)) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.addSet();
      }, { capture: true, signal });
      this.root.addEventListener('click', event => {
        const button = event.target.closest('button[type="submit"][name="add"]');
        if (this.count < 2 || !button || !this.isPurchaseForm(button.form)) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.addSet();
      }, { capture: true, signal });
      this.dataset.packReady = '';
      this.hidden = false;
      this.editorStatus(this.isDiscountPreview() ? 'Editor preview only: these savings are configured in the theme but are not verified active Shopify discounts. Customers see regular prices until matching discounts are active and verified.' : '');
      return true;
    }

    disconnectedCallback() {
      this.stopInitialization();
      this.lifecycleAbort?.abort();
      this.lifecycleAbort = null;
      this.abort?.abort();
      this.abort = null;
      this.restoreNative();
      this.stickyButton?.remove();
      delete this.dataset.packReady;
    }

    isDiscountPreview() {
      return this.data.verified !== true && this.data.designMode === true && this.data.previewDiscounts === true;
    }

    discountDisplayEnabled() {
      return this.data.verified === true || this.isDiscountPreview();
    }

    currentVariant() {
      const id = this.mainForm?.querySelector('[name="id"]')?.value || this.data.initialVariantId;
      return this.data.variants.find(variant => String(variant.id) === String(id)) || this.data.variants.find(variant => String(variant.id) === String(this.data.initialVariantId)) || this.data.variants[0];
    }

    isPurchaseForm(form) {
      return !!form && (form === this.mainForm || form.id === `sticky-addcart-form-${this.dataset.sectionId}`);
    }

    createStickyButton(signal) {
      const area = this.root.querySelector('sticky-add-cart .product-detail__buy-buttons');
      if (!area) return;
      this.stickyButton = document.createElement('button');
      this.stickyButton.dataset.hemkindStyle = this.dataset.hemkindStyle || '';
      this.stickyButton.type = 'button';
      this.stickyButton.className = 'btn btn-primary hk-packs__sticky';
      this.stickyButton.textContent = this.copy('add_set');
      this.stickyButton.hidden = true;
      this.stickyButton.setAttribute('aria-controls', this.id);
      area.after(this.stickyButton);
      this.stickyButton.addEventListener('click', () => this.addSet(), { signal });
    }

    restoreNative() {
      (this.nativeAreas || []).forEach(area => area.classList.remove('hk-packs__native-hidden'));
      this.root?.querySelectorAll('sticky-add-cart .product-detail__variant-picker').forEach(area => area.classList.remove('hk-packs__native-hidden'));
    }

    applyMode() {
      const pack = this.count > 1;
      this.builder.hidden = !pack;
      this.nativeAreas.forEach(area => area.classList.toggle('hk-packs__native-hidden', pack));
      this.root.querySelectorAll('sticky-add-cart .product-detail__variant-picker').forEach(area => area.classList.toggle('hk-packs__native-hidden', pack));
      if (this.stickyButton) this.stickyButton.hidden = !pack;
    }

    onChange(event) {
      if (event.target.matches('.hk-packs__quantities input')) {
        if (this.busy) return;
        const nextCount = Number(event.target.value);
        if (!this.allowedQuantities.includes(nextCount)) return;
        this.count = nextCount;
        const current = this.currentVariant();
        if (!this.selections.length) this.sharedSize.value = current.size;
        while (this.selections.length < this.count) this.selections.push({ productId: current.productId, color: current.color, size: this.sharedSize.value });
        this.applyMode(); this.renderSlots();
      } else if (event.target === this.sharedSize) {
        this.selections.forEach(selection => { selection.size = this.sharedSize.value; });
        this.renderSlots();
      } else if (event.target.matches('[data-slot-color], [data-slot-size]')) {
        const index = Number(event.target.closest('[data-slot]').dataset.slot);
        const selection = this.selections[index];
        if (event.target.hasAttribute('data-slot-color')) {
          const chosen = this.colors.find(color => color.key === event.target.value);
          selection.productId = chosen.productId; selection.color = chosen.color;
        } else selection.size = event.target.value;
        this.updateSlot(index); this.updateSummary();
      }
    }

    selectedVariant(selection) {
      return this.data.variants.find(variant => String(variant.productId) === String(selection.productId) && variant.color === selection.color && variant.size === selection.size);
    }

    selectedVariants() {
      return this.selections.slice(0, this.count).map(selection => this.selectedVariant(selection));
    }

    renderSlots() {
      this.slots.replaceChildren();
      for (let index = 0; index < this.count; index++) {
        const slot = document.createElement('div');
        slot.className = 'hk-packs__piece'; slot.dataset.slot = String(index);
        const picture = document.createElement('img');
        picture.width = 72; picture.height = 90; picture.loading = 'lazy';
        const body = document.createElement('div'); body.className = 'hk-packs__piece-body';
        const title = document.createElement('strong'); title.textContent = this.copy('piece', { INDEX: index + 1 });
        const controls = document.createElement('div'); controls.className = 'hk-packs__piece-controls';
        for (const kind of ['color', 'size']) {
          const label = document.createElement('label'); label.textContent = this.copy(kind);
          const select = document.createElement('select'); select.setAttribute(`data-slot-${kind}`, '');
          select.setAttribute('aria-label', this.copy(kind === 'color' ? 'piece_color' : 'piece_size', { INDEX: index + 1 }));
          if (kind === 'color') {
            for (const color of this.colors) select.add(new Option(color.color, color.key));
          }
          label.append(select); controls.append(label);
        }
        const status = document.createElement('p'); status.className = 'hk-packs__piece-status'; status.setAttribute('aria-live', 'polite');
        body.append(title, controls, status); slot.append(picture, body); this.slots.append(slot); this.updateSlot(index);
      }
      this.updateSummary();
    }

    updateSlot(index) {
      const slot = this.slots.querySelector(`[data-slot="${index}"]`);
      const selection = this.selections[index];
      const choices = this.data.variants.filter(variant => String(variant.productId) === String(selection.productId) && variant.color === selection.color);
      slot.querySelector('[data-slot-color]').value = `${selection.productId}:${selection.color}`;
      const sizeSelect = slot.querySelector('[data-slot-size]');
      sizeSelect.replaceChildren();
      const sizes = [...new Set(choices.map(variant => variant.size))];
      if (!sizes.includes(selection.size)) sizeSelect.add(new Option(this.copy('unavailable_size', { SIZE: selection.size }), selection.size));
      for (const size of sizes) {
        const option = new Option(size, size);
        if (!choices.some(variant => variant.size === size && variant.available)) option.textContent = this.copy('sold_out_size', { SIZE: size });
        sizeSelect.add(option);
      }
      sizeSelect.value = selection.size;
      const variant = this.selectedVariant(selection);
      const image = variant?.image || choices.find(value => value.image)?.image;
      const picture = slot.querySelector('img'); picture.hidden = !image;
      if (image) picture.src = image;
      picture.alt = this.copy('piece_image', { COLOR: selection.color, INDEX: index + 1 });
      slot.querySelector('.hk-packs__piece-status').textContent = variant?.available ? '' : this.copy('combination_unavailable');
    }

    money(cents) {
      try { return new Intl.NumberFormat(this.data.locale || 'en', { style: 'currency', currency: this.data.currency }).format(cents / 100); }
      catch (_) { return `${this.data.currency} ${(cents / 100).toFixed(2)}`; }
    }

    updateSummary() {
      const variants = this.selectedVariants();
      let message = '';
      try {
        const items = HemkindPacks.aggregate(variants, this.copy.bind(this));
        HemkindPacks.assertQuantities(items, this.data.variants, { items: [] }, this.copy.bind(this));
      } catch (error) { message = error.message; }
      const rate = HemkindPacks.rateFor(this.count, this.data.rates, this.discountDisplayEnabled());
      const quote = HemkindPacks.quote(variants, rate);
      const complete = variants.every(Boolean);
      const original = this.querySelector('[data-pack-original]');
      original.hidden = !complete || !quote.saving; original.textContent = this.money(quote.original);
      this.querySelector('[data-pack-total]').textContent = complete ? this.money(quote.total) : this.copy('choose_every_piece');
      const saving = this.querySelector('[data-pack-saving]');
      saving.hidden = !complete || !quote.saving;
      saving.textContent = this.copy('saving_summary', { SAVING: this.money(quote.saving), RATE: rate, AVERAGE: this.money(Math.round(quote.total / this.count)) });
      this.setError(message);
      this.invalid = !!message;
      this.updateButtons();
    }

    setError(message) {
      this.errorNode.textContent = message;
      this.errorNode.hidden = !message;
    }

    updateButtons() {
      for (const button of [this.addButton, this.stickyButton].filter(Boolean)) {
        button.disabled = this.busy || this.invalid || this.uncertain;
        button.textContent = this.busy ? this.copy('adding_set') : this.copy('add_set');
      }
      this.querySelectorAll('select, .hk-packs__quantities input').forEach(input => { input.disabled = this.busy; });
      this.setAttribute('aria-busy', String(this.busy));
    }

    async readCart() {
      const response = await fetch(`${this.data.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(this.copy('cart_check_failed'));
      return response.json();
    }

    async addSet() {
      if (this.count < 2 || !this.allowedQuantities.includes(this.count) || this.busy || this.invalid || this.uncertain) return;
      this.busy = true; this.updateButtons(); this.setError(''); this.cartLink.hidden = true;
      let items; let before; let submitted = false; let accepted = false; let confirmedRejection = false;
      try {
        items = HemkindPacks.aggregate(this.selectedVariants(), this.copy.bind(this));
        before = await this.readCart();
        HemkindPacks.assertQuantities(items, this.data.variants, before, this.copy.bind(this));
        const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sectionIds = typeof cartUI?.getSectionsToRender === 'function' ? cartUI.getSectionsToRender().map(section => section.id).slice(0, 5) : [];
        const body = { items, sections_url: window.location.pathname };
        if (sectionIds.length) body.sections = sectionIds;
        cartUI?.setActiveElement?.(document.activeElement);
        submitted = true;
        const response = await fetch(`${this.data.cartAddUrl}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        const result = await response.json();
        if (!response.ok || result.status) {
          const status = Number(response.status || result.status);
          confirmedRejection = status >= 400 && status < 500 && status !== 408;
          throw new Error(typeof result.description === 'string' ? result.description : this.copy('add_failed'));
        }
        accepted = true;
        this.cartLink.hidden = false;
        const cart = await this.readCart();
        this.refreshCounts(cart);
        if (!await this.renderCart(cartUI, result)) window.location.assign(this.data.cartUrl);
        this.dispatchEvent(new CustomEvent('hemkind:pack-added', { bubbles: true, detail: { items, cart } }));
      } catch (error) {
        if (accepted) {
          // A successful add must never be submitted again because the drawer failed.
          this.uncertain = true;
          this.cartLink.hidden = false;
          this.setError(this.copy('added_review'));
        } else if (submitted) {
          try {
            const after = await this.readCart();
            this.refreshCounts(after);
            const changed = items.some(item => HemkindPacks.cartQuantity(after, item.id) > HemkindPacks.cartQuantity(before, item.id));
            // A lost response may precede a delayed server commit, even if this GET is unchanged.
            this.uncertain = changed || !confirmedRejection;
            this.cartLink.hidden = !this.uncertain;
            this.setError(changed ? this.copy('cart_changed') : confirmedRejection ? error.message : this.copy('addition_uncertain'));
          } catch (_) {
            this.uncertain = true;
            this.cartLink.hidden = false;
            this.setError(this.copy('addition_uncertain'));
          }
        } else this.setError(error.message);
      } finally {
        this.busy = false;
        this.updateButtons();
      }
    }

    refreshCounts(cart) {
      document.querySelectorAll('.cart-count').forEach(node => {
        node.textContent = node.classList.contains('cart-count-drawer') ? `(${cart.item_count})` : cart.item_count > 100 ? '~' : String(cart.item_count);
      });
      document.querySelector('header-total-price')?.updateTotal?.(cart);
      document.querySelector('free-ship-progress-bar')?.init?.(cart.items_subtotal_price);
    }

    async renderCart(cartUI, result) {
      const section = result.sections?.['minicart-form'];
      if (!cartUI || typeof section !== 'string' || typeof cartUI.cartAction !== 'function' || typeof cartUI.open !== 'function') return false;
      const parsed = new DOMParser().parseFromString(section, 'text/html').querySelector('#minicart-form');
      const current = cartUI.querySelector('#minicart-form') || document.querySelector('#minicart-form');
      if (!parsed || !current) return false;
      current.innerHTML = parsed.innerHTML;
      cartUI.classList.remove('is-empty');
      await cartUI.cartAction();
      await cartUI.open();
      return true;
    }
  }
  customElements.define('hemkind-packs', HemkindPacks);
})();


